import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useShellVariant } from '@/components/layout/shell-context';
import { Link } from 'react-router-dom';
import { formatQty, PERMISSIONS, type Paginated } from '@erp/shared';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ClipboardList, Clock, PackageSearch, Printer, ScanLine
} from 'lucide-react';
import { ApiError, api, errorMessage, openPdf } from '@/lib/api';

import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { useEventStream } from '@/hooks/useEventStream';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, ProgressBar } from '@/components/ui/misc';
import { toast } from 'sonner';
import {
  TRAFFIC_CLASSES, TRAFFIC_EPSILON, excessSignature, formatAge, isOverScanned, lastLiveScanForLine,
  lineTraffic, needsExcessAck, oldestFirst, parseQuantity, pendingPieces
} from './traffic';

interface WarehouseKit {
  id: string; number: string; status: string; created_at: string;
  order_number: string | null; model_name: string; total_lines: number; pending_lines: number;
  age_hours: number; aged: boolean; required_count: number; scanned_count: number;
}

interface KitLine {
  id: string; productId: string; sku: string; name: string;
  requiredQty: number; scannedQty: number; pendingQty: number;
}

interface KitScan {
  id: string; quantity: number; productId: string; kitLineId: string; createdAt: string; voidedAt: string | null;
}

interface KitDetail {
  id: string; number: string; status: string; lines: KitLine[]; missingLines: number;
  progress: number; trackingLabelUrl: string; orderNumber: string | null; modelName: string;
  canComplete: boolean; createdAt: string; scans: KitScan[];
}

interface ScanResult {
  product: { id: string; sku: string; name: string };
  lineId: string; scannedQty: number; requiredQty: number; pendingQty: number;
  complete: boolean; isSubstitution?: boolean;
}

interface Feedback { tone: 'success' | 'warning' | 'error'; title: string; message: string }

/**
 * Datos del exceso para el aviso: los que manda la API en `details` (funciona también cuando el
 * operario escanea el EAN) y, si no vienen, la línea buscada por SKU.
 */
function overscanContext(kit: KitDetail | undefined, error: unknown, code: string) {
  if (error instanceof ApiError && error.code === 'SCAN_EXCESSIVE') {
    const details = error.details as { sku?: string; required?: number; pending?: number } | undefined;
    if (details?.sku) {
      return {
        sku: details.sku,
        required: formatQty(details.required ?? 0),
        pending: formatQty(details.pending ?? 0)
      };
    }
  }
  const line = kit?.lines.find((item) => item.sku.toUpperCase() === code.trim().toUpperCase());
  if (!line) return null;
  return { required: formatQty(line.requiredQty), pending: formatQty(line.pendingQty), sku: line.sku };
}

export function WarehousePage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [ageAlert, setAgeAlert] = useState<{ kit: string; hours: number; count: number } | null>(null);
  const agedPending = useRef<{ kit: string; hours: number; count: number } | null>(null);
  const agedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const variant = useShellVariant();

  const kitsQuery = useQuery({
    // Orden por antigüedad en el servidor (`sort=age`): pedir «los 50 de siempre» y ordenarlos en
    // cliente dejaría fuera justo los kits viejos en cuanto la cola pasa de 50.
    queryKey: ['kits', 'warehouse'],
    queryFn: () => api<Paginated<WarehouseKit> & { meta: { agedTotal?: number } }>('/kits?status=open&sort=age&pageSize=50'),
    // La cola se refresca con el SSE; no hay polling desde el navegador.
    staleTime: 30_000
  });

  // Aviso de antigüedad por SSE: la API publica `kit.aged` cuando un kit cruza el umbral. La
  // conexión es la misma que ya tiene el AppShell: no se abre un segundo EventSource. Una ráfaga
  // (varios kits a la vez, o todos los atrasados tras un reinicio del contenedor) se agrupa en un
  // único aviso: un toast por kit tapaba la pantalla y no aportaba nada que no diga el contador.
  useEventStream(can(PERMISSIONS.KITS_READ), (event) => {
    if (event.type !== 'kit.aged') return;
    const kit = String(event.data?.kit ?? '');
    const hours = Number(event.data?.ageHours ?? event.data?.thresholdHours ?? 0);
    agedPending.current = { kit, hours, count: (agedPending.current?.count ?? 0) + 1 };
    if (agedTimer.current) return;
    agedTimer.current = setTimeout(() => {
      agedTimer.current = null;
      const batch = agedPending.current;
      // La ráfaga se cierra aquí: si no, la siguiente la suma a la cuenta anterior y el aviso
      // diría «N kits» con un N acumulado de hace horas.
      agedPending.current = null;
      if (batch) setAgeAlert(batch);
    }, 400);
  });

  useEffect(() => () => {
    if (agedTimer.current) clearTimeout(agedTimer.current);
  }, []);

  const rows = oldestFirst(kitsQuery.data?.data ?? []);
  // Los contadores son de la cola entera (meta.total / meta.agedTotal), no de esta página.
  const total = kitsQuery.data?.meta.total ?? rows.length;
  const aged = kitsQuery.data?.meta.agedTotal ?? rows.filter((row) => row.aged).length;

  const select = (id: string) => {
    setSelected(id);
    window.setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  if (kitsQuery.error) {
    return <ErrorState message={(kitsQuery.error as Error).message} onRetry={() => void kitsQuery.refetch()} />;
  }

  return (
    <div
      className={variant === 'station'
        ? 'flex w-full flex-col gap-5 pb-6'
        : 'mx-auto flex w-full max-w-2xl flex-col gap-5 pb-24'}
    >
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">{t('warehouse.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('warehouse.subtitle')}</p>
      </header>

      {ageAlert ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-400 bg-amber-50 p-4 text-amber-900" role="status">
          <Clock className="mt-0.5 size-5 shrink-0" />
          <p className="text-sm font-medium">
            {ageAlert.count > 1
              ? t('warehouse.ageAlertMany', { count: ageAlert.count })
              : t('warehouse.ageAlert', { kit: ageAlert.kit, hours: Math.round(ageAlert.hours) })}
          </p>
          <button type="button" className="ml-auto text-xs underline" onClick={() => setAgeAlert(null)}>
            {t('actions.close')}
          </button>
        </div>
      ) : null}

      <section className="flex flex-col gap-3" aria-label={t('warehouse.queue')}>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('warehouse.queue')}</h2>
          <div className="flex items-center gap-2 text-xs">
            <Badge tone="neutral">{t('warehouse.pendingCount', { count: total })}</Badge>
            {aged > 0 ? <Badge tone="warning">{t('warehouse.agedCount', { count: aged })}</Badge> : null}
          </div>
        </div>

        {kitsQuery.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-24 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState title={t('warehouse.empty')} description={t('warehouse.emptyHint')} icon={PackageSearch} />
        ) : (
          <ul className={variant === 'station' ? 'grid gap-3 sm:grid-cols-2 2xl:grid-cols-3' : 'flex flex-col gap-3'}>
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => select(row.id)}
                  className={cn(
                    'flex w-full flex-col gap-2 rounded-lg border p-4 text-left transition-colors',
                    // Objetivo táctil holgado y alto mínimo de 44 px para el guante de almacén.
                    'min-h-[44px] hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    row.aged ? 'border-amber-400 bg-amber-50' : 'border-border bg-card',
                    selected === row.id && 'ring-2 ring-primary'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-lg font-semibold">{row.number}</span>
                    <StatusBadge status={row.status} />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {row.order_number ?? '—'} · {row.model_name}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">
                      {t('warehouse.missingPieces', { count: pendingPieces(row.pending_lines) })}
                    </span>
                    <span className="text-muted-foreground">
                      {t('warehouse.ofLines', {
                        done: row.total_lines - pendingPieces(row.pending_lines), total: row.total_lines
                      })}
                    </span>
                    <span className={cn('ml-auto inline-flex items-center gap-1', row.aged ? 'font-semibold text-amber-800' : 'text-muted-foreground')}>
                      <Clock className="size-4" /> {formatAge(row.age_hours)}
                      {row.aged ? <span className="rounded bg-amber-200 px-1 text-xs">+{t('warehouse.aged')}</span> : null}
                    </span>
                  </div>
                  <span className="mt-1 inline-flex items-center gap-2 text-sm font-medium text-primary">
                    <ScanLine className="size-4" /> {t('warehouse.prepare')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div ref={panelRef}>
        {selected ? (
          <WarehouseKitPanel key={selected} kitId={selected} onClose={() => setSelected(null)} />
        ) : (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            {t('warehouse.pickHint')}
          </p>
        )}
      </div>

      <section className="flex flex-col gap-2" aria-label={t('warehouse.shortcuts')}>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('warehouse.shortcuts')}</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {can(PERMISSIONS.PURCHASES_READ) ? (
            <Link
              to="/invoices"
              className="flex min-h-[44px] items-center gap-2 rounded-lg border bg-card px-4 py-3 text-sm font-medium hover:border-primary"
            >
              <ClipboardList className="size-5" /> {t('warehouse.reception')}
            </Link>
          ) : null}
          {can(PERMISSIONS.PACKAGING_READ) ? (
            <Link
              to="/packaging"
              className="flex min-h-[44px] items-center gap-2 rounded-lg border bg-card px-4 py-3 text-sm font-medium hover:border-primary"
            >
              <Printer className="size-5" /> {t('warehouse.printing')}
            </Link>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function WarehouseKitPanel({ kitId, onClose }: { kitId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [busy, setBusy] = useState(false);
  // El exceso por debajo de 5× lo acepta la API (el consumo del kit se calcula desde los
  // escaneos reales, así que se consume lo escaneado). Para que un doble disparo del lector no
  // cierre el kit con una pieza de más sin que nadie lo vea, el cierre exige reconocer el exceso:
  // se guarda la firma reconocida, no un booleano, para que un exceso nuevo la invalide.
  const [excessAckFor, setExcessAckFor] = useState<string | null>(null);
  const [undoing, setUndoing] = useState<string | null>(null);

  const kitQuery = useQuery({
    queryKey: ['kit', kitId],
    queryFn: () => api<KitDetail>(`/kits/${kitId}`)
  });
  const kit = kitQuery.data;

  useEffect(() => {
    if (kit && kit.status !== 'completed') inputRef.current?.focus();
  }, [kit?.status, kit?.id]);

  const scan = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!kit || !code.trim()) return;
    const amount = parseQuantity(quantity);
    if (amount == null) {
      // «2,4» se lee bien; lo que no es un número positivo se avisa, no se cambia por 1.
      setFeedback({ tone: 'error', title: t('warehouse.scanRejected'), message: t('warehouse.invalidQty') });
      return;
    }
    setBusy(true);
    try {
      const result = await api<ScanResult>(`/kits/${kit.id}/scan`, {
        method: 'POST',
        body: { ean: code.trim(), quantity: amount }
      });
      // La API solo rechaza a partir de 5×: un exceso menor se acepta, así que se avisa aquí.
      const excess = result.scannedQty > result.requiredQty + TRAFFIC_EPSILON;
      setFeedback({
        tone: excess ? 'warning' : result.complete ? 'success' : 'warning',
        title: excess
          ? t('warehouse.overScanned')
          : result.complete ? t('warehouse.lineComplete') : t('warehouse.linePartial'),
        message: excess
          ? t('warehouse.overScannedDetail', {
            sku: result.product.sku,
            scanned: formatQty(result.scannedQty),
            required: formatQty(result.requiredQty)
          })
          : t('warehouse.scannedLine', {
            sku: result.product.sku, scanned: formatQty(result.scannedQty),
            required: formatQty(result.requiredQty), pending: formatQty(result.pendingQty)
          })
      });
      setCode('');
      await qc.invalidateQueries({ queryKey: ['kit', kitId] });
      await qc.invalidateQueries({ queryKey: ['kits'] });
    } catch (error) {
      // Escanear de más (o un código que no es del kit) se avisa en el panel de la
      // pantalla, no con un alert del navegador, y la API sigue rechazándolo.
      const extra = overscanContext(kit, error, code);
      setFeedback({
        tone: 'error',
        title: t('warehouse.scanRejected'),
        message: extra
          ? t('warehouse.scanExcess', { ...extra, message: errorMessage(error) })
          : errorMessage(error)
      });
      setCode('');
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  /** Anula el último escaneo vivo de una línea con exceso: el doble disparo del lector. */
  const undoExcess = async (line: KitLine) => {
    if (!kit) return;
    const lastScan = lastLiveScanForLine(kit.scans, line.id);
    if (!lastScan) {
      // Sin escaneo vivo no hay nada que deshacer, pero el operario tiene que saberlo: un botón
      // que no hace nada se lee como una pantalla rota.
      setFeedback({
        tone: 'error',
        title: t('warehouse.undoNoScan'),
        message: t('warehouse.undoNoScanDetail', { sku: line.sku })
      });
      return;
    }
    setUndoing(line.id);
    try {
      // Un escaneo puede traer más cantidad que el exceso (una entrada manual de 5 con 1 de más):
      // anularlo dejaría la línea por debajo de lo requerido, así que desde el puesto solo se
      // deshacen los que devuelven la línea a su cantidad requerida. El resto se corrige en la
      // ficha del kit, donde se anula un escaneo concreto con su motivo.
      const excessQty = Math.max(0, line.scannedQty - line.requiredQty);
      if (lastScan.quantity > excessQty + TRAFFIC_EPSILON) {
        setFeedback({
          tone: 'warning',
          title: t('warehouse.undoTooMuch'),
          message: t('warehouse.undoTooMuchDetail', {
            qty: formatQty(lastScan.quantity), excess: formatQty(excessQty), sku: line.sku
          })
        });
        return;
      }
      await api(`/kits/${kit.id}/scans/${lastScan.id}/void`, {
        method: 'POST',
        body: { reason: t('warehouse.undoReason') }
      });
      setFeedback({
        tone: 'success',
        title: t('warehouse.undoDone'),
        message: t('warehouse.undoDoneDetail', { sku: line.sku, qty: formatQty(lastScan.quantity) })
      });
      // El exceso cambia, así que el reconocimiento guardado deja de valer.
      setExcessAckFor(null);
      await qc.invalidateQueries({ queryKey: ['kit', kitId] });
      await qc.invalidateQueries({ queryKey: ['kits'] });
    } catch (error) {
      setFeedback({ tone: 'error', title: t('warehouse.undoFailed'), message: errorMessage(error) });
    } finally {
      setUndoing(null);
    }
  };

  const complete = async () => {
    if (!kit) return;
    // La ventana se abre dentro del gesto del usuario: si se abre tras los `await`, el navegador
    // la bloquea sin lanzar error y la etiqueta no se imprime sin que nadie se entere.
    const labelWindow = window.open('', '_blank');
    setBusy(true);
    try {
      const result = await api<{ trackingLabelUrl: string; message: string }>(`/kits/${kit.id}/complete`, { method: 'POST' });
      toast.success(result.message);
      const url = result.trackingLabelUrl.startsWith('/api') ? result.trackingLabelUrl : `/api${result.trackingLabelUrl}`;
      if (labelWindow) {
        labelWindow.location.href = url;
      } else {
        // Bloqueada: se intenta por blob y, si tampoco, queda el botón «Etiqueta de seguimiento».
        try {
          await openPdf(result.trackingLabelUrl);
        } catch {
          toast.error(t('warehouse.labelError'));
        }
      }
      await qc.invalidateQueries({ queryKey: ['kits'] });
      onClose();
    } catch (error) {
      labelWindow?.close();
      setFeedback({ tone: 'error', title: t('warehouse.completeRejected'), message: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  if (kitQuery.error) return <ErrorState message={(kitQuery.error as Error).message} onRetry={() => void kitQuery.refetch()} />;
  if (kitQuery.isLoading || !kit) return <div className="h-40 animate-pulse rounded-lg bg-muted" />;

  const workable = kit.status !== 'completed' && kit.status !== 'cancelled';
  const excessLines = kit.lines.filter(isOverScanned);
  const excessQty = excessLines.reduce(
    (sum, line) => sum + Math.max(0, line.scannedQty - line.requiredQty), 0
  );
  const excessAck = excessSignature(kit.lines);
  const needsAck = needsExcessAck(kit.lines, excessAckFor);
  const tone = feedback?.tone === 'success' ? 'border-emerald-400 bg-emerald-50 text-emerald-900'
    : feedback?.tone === 'warning' ? 'border-amber-400 bg-amber-50 text-amber-900'
      : 'border-red-400 bg-red-50 text-red-900';

  return (
    <Card className="mt-2">
      <CardHeader
        title={kit.number}
        description={`${kit.orderNumber ?? '—'} · ${kit.modelName}`}
        actions={(
          <Button variant="ghost" size="sm" onClick={onClose} className="min-h-[44px]">
            <ArrowLeft /> {t('warehouse.back')}
          </Button>
        )}
      />
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <ProgressBar value={kit.progress} tone={kit.missingLines === 0 ? 'success' : 'warning'} />
          <p className="text-sm text-muted-foreground">
            {t('warehouse.linesPending', { count: kit.missingLines })}
            {' · '}
            {t('warehouse.age')}: {formatAge(Math.max(0, (Date.now() - new Date(kit.createdAt).getTime()) / 3_600_000))}
          </p>
        </div>
        {feedback ? (
          <div
            className={cn('flex items-start gap-3 rounded-lg border p-4', tone)}
            // Asertivo solo cuando hay algo que corregir: una lectura buena no debe interrumpir
            // al lector de pantalla.
            role={feedback.tone === 'success' ? 'status' : 'alert'}
          >
            {feedback.tone === 'success' ? <CheckCircle2 className="mt-0.5 size-5 shrink-0" />
              : <AlertTriangle className="mt-0.5 size-5 shrink-0" />}
            <div className="text-sm">
              <p className="font-semibold">{feedback.title}</p>
              <p>{feedback.message}</p>
            </div>
          </div>
        ) : null}

        <form onSubmit={scan} className="flex flex-col gap-2">
          <label className="text-sm font-medium" htmlFor="warehouse-scan">{t('warehouse.scanLabel')}</label>
          <div className="flex gap-2">
            <Input
              id="warehouse-scan"
              ref={inputRef}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder={t('warehouse.scanPlaceholder')}
              autoComplete="off"
              className="h-12 text-base"
              disabled={!workable || !can(PERMISSIONS.KITS_SCAN) || busy}
            />
            <Input
              aria-label={t('common.quantity')}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              inputMode="decimal"
              className="h-12 w-20 text-base"
              disabled={!workable || !can(PERMISSIONS.KITS_SCAN) || busy}
            />
            <Button
              type="submit"
              size="lg"
              className="h-12 min-w-[44px]"
              disabled={!workable || !can(PERMISSIONS.KITS_SCAN) || busy}
            >
              <ScanLine /> {t('actions.scan')}
            </Button>
          </div>
        </form>

        <ul className="flex flex-col gap-2" aria-label={t('warehouse.pieces')}>
          {kit.lines.map((line) => {
            const light = lineTraffic(line);
            const classes = TRAFFIC_CLASSES[light];
            const excess = isOverScanned(line);
            return (
              <li
                key={line.id}
                className={cn('flex items-center gap-3 rounded-lg border p-3', classes.row)}
                data-traffic={light}
                data-excess={excess ? '1' : undefined}
              >
                <span className={cn('size-4 shrink-0 rounded-full', classes.dot)} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{line.name}</p>
                  <p className={cn('text-xs', classes.label)}>{line.sku}</p>
                </div>
                <div className="text-right text-sm tabular-nums">
                  <p className="font-semibold">{formatQty(line.scannedQty)} / {formatQty(line.requiredQty)}</p>
                  <p className={cn('text-xs', classes.label)}>
                    {excess
                      ? t('warehouse.excessShort', { qty: formatQty(line.scannedQty - line.requiredQty) })
                      : light === 'full'
                        ? t('warehouse.ready')
                        : t('warehouse.missingQty', { missing: formatQty(line.pendingQty) })}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>

        {needsAck ? (
          <div
            className="flex flex-col gap-2 rounded-lg border border-amber-400 bg-amber-50 p-4 text-amber-900"
            role="alert"
            data-excess-block={excessLines.length}
          >
            <p className="flex items-center gap-2 text-sm font-semibold">
              <AlertTriangle className="size-5 shrink-0" />
              {t('warehouse.excessBlockTitle', { count: excessLines.length })}
            </p>
            <p className="text-sm">{t('warehouse.excessBlockDetail', { qty: formatQty(excessQty) })}</p>
            <ul className="flex flex-col gap-2">
              {excessLines.map((line) => (
                <li key={line.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="font-mono text-xs">{line.sku}</span>
                  {can(PERMISSIONS.KITS_SCAN) ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={undoing !== null || busy}
                      onClick={() => void undoExcess(line)}
                    >
                      {undoing === line.id ? t('warehouse.undoing') : t('warehouse.undoLast')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
            <Button size="lg" className="h-12" disabled={busy} onClick={() => setExcessAckFor(excessAck)}>
              <CheckCircle2 /> {t('warehouse.excessConfirm')}
            </Button>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            size="lg"
            className="h-12 flex-1"
            disabled={!workable || !kit.canComplete || !can(PERMISSIONS.KITS_COMPLETE) || busy || needsAck}
            onClick={() => void complete()}
          >
            <CheckCircle2 /> {t('warehouse.complete')}
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="h-12 flex-1"
            onClick={() => void openPdf(kit.trackingLabelUrl)}
          >
            <Printer /> {t('warehouse.printLabel')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
